import { Button, Checkbox, FormGroup, HTMLSelect } from '@blueprintjs/core'
import * as React from 'react'
import { CSSProperties } from 'react'
import { useBlockLayout, useResizeColumns, useSortBy, useTable } from 'react-table'
import Spinner from '../../../components/Spinner'
import { getSort } from '../../../utils'

const paginationButtonStyle: CSSProperties = {
  width: 100,
  textAlign: 'center'
}

const SELECTION_COLUMN_WIDTH = 40

const SELECTION_COLUMN_STYLE: CSSProperties = {
  width: SELECTION_COLUMN_WIDTH,
  minWidth: SELECTION_COLUMN_WIDTH,
  maxWidth: SELECTION_COLUMN_WIDTH
}

const style = {
  pagination: {
    button: paginationButtonStyle,
    pageSize: {
      marginLeft: 20,
      marginRight: 20,
      marginBottom: 0
    },
    div: {
      marginTop: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }
}

const CursorPaginatedTable = ({
  meta,
  onMetaChange,
  data,
  idKey,
  columns,
  loading,
  isSelected,
  toggleSelection,
  toggleAll,
  selectAll,
  ...rest
}) => {
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({})

  const visibleColumns = React.useMemo(() => columns.filter(c => c.show !== false), [columns])
  const metaSort = meta?.sort
  const tableData = React.useMemo(
    () =>
      data.map(entry => ({
        _id: entry[idKey || 'id'],
        ...entry
      })),
    [data, idKey]
  )

  const initialSortBy = React.useMemo(() => {
    const sort = getSort(metaSort)
    if (!sort || !sort.length) return []
    return sort.map(s => ({ id: s.id, desc: s.desc }))
  }, [metaSort])

  const columnsWithWidths = React.useMemo(
    () =>
      visibleColumns.map(col => ({
        ...col,
        disableSortBy: col.sortable === false,
        width: columnWidths[col.id] ?? col.width
      })),
    [visibleColumns, columnWidths]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
    {
      columns: columnsWithWidths,
      data: tableData,
      manualSortBy: true,
      autoResetSortBy: false,
      initialState: {
        sortBy: initialSortBy
      } as any,
      getRowId: (row: any) => String(row._id),
      ...rest
    },
    useBlockLayout,
    useResizeColumns,
    useSortBy
  ) as any

  const { sortBy, columnResizing } = state as any
  const sortValue = sortBy.length ? `${sortBy[0].id}.${sortBy[0].desc ? 'desc' : 'asc'}` : undefined

  React.useEffect(() => {
    if (columnResizing.isResizingColumn) return

    const newWidths = columnResizing.columnWidths
    if (newWidths && Object.keys(newWidths).length > 0) {
      setColumnWidths(prev => ({ ...prev, ...newWidths }))
    }
  }, [columnResizing])

  const lastPushedSort = React.useRef<string | undefined>(metaSort)

  React.useEffect(() => {
    if (!sortValue) return
    if (sortValue === lastPushedSort.current) return
    lastPushedSort.current = sortValue
    onMetaChange('sort', sortValue)
  }, [sortValue, onMetaChange])

  const getColumnStyles = (column: any) => {
    const styles: React.CSSProperties = {}
    if (column.width) {
      styles.width = column.width
      styles.maxWidth = column.width
      styles.minWidth = column.width
    } else {
      if (column.minWidth) styles.minWidth = column.minWidth
      if (column.maxWidth) styles.maxWidth = column.maxWidth
    }
    return styles
  }

  const renderColumnHeader = (column: any) => {
    const view = column.render('Header')
    if (column.id === 'thumb' && !view) {
      return <span style={{ visibility: 'hidden' }}>Thumbnail</span>
    }

    return view
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <div className="listing-manager-table-scroll">
        <div className="ReactTable -striped">
          <table {...getTableProps()} className="rt-table">
            <thead className="rt-thead -header">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                  <th className="rt-th" style={SELECTION_COLUMN_STYLE}>
                    <Checkbox checked={selectAll} onChange={toggleAll} />
                  </th>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps()}
                      key={column.id}
                      className={[
                        'rt-th',
                        (column as any).isSorted && ((column as any).isSortedDesc ? '-sort-desc' : '-sort-asc'),
                        (column as any).canSort && '-cursor-pointer'
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ ...getColumnStyles(column), position: 'relative', overflow: 'hidden' }}
                    >
                      <div className="listing-manager-header-cell-content">
                        <div
                          {...(column as any).getSortByToggleProps()}
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {renderColumnHeader(column)}
                        </div>
                      </div>
                      {(column as any).canResize ? (
                        <div
                          {...(column as any).getResizerProps()}
                          onClick={e => e.stopPropagation()}
                          className={`listing-manager-column-resizer ${(column as any).isResizing ? 'isResizing' : ''}`}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <thead className="rt-thead -filters">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                  <th className="rt-th" style={SELECTION_COLUMN_STYLE} />
                  {headerGroup.headers.map(column => (
                    <th
                      key={column.id}
                      className="rt-th"
                      style={{
                        ...getColumnStyles(column),
                        verticalAlign: 'middle',
                        padding: '4px 8px'
                      }}
                    >
                      {(column as any).filterable && (column as any).Filter ? (column as any).render('Filter') : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className="rt-tbody">
              {rows.map((row, rowIndex) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} className={`rt-tr ${rowIndex % 2 === 0 ? '-odd' : '-even'}`}>
                    <td className="rt-td" style={SELECTION_COLUMN_STYLE}>
                      <Checkbox
                        checked={isSelected(row.original._id)}
                        onChange={() => toggleSelection(row.original._id)}
                      />
                    </td>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()} className="rt-td" style={getColumnStyles(cell.column)}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="listing-manager-table-pagination">
        <Button
          large
          style={style.pagination.button}
          onClick={() => onMetaChange('cursor', meta?.previousCursor)}
          disabled={!meta?.previousCursor}
          loading={!!loading}
        >
          Previous
        </Button>{' '}
        <FormGroup inline label="Page size" style={style.pagination.pageSize}>
          <HTMLSelect
            options={[{ value: 10 }, { value: 15 }, { value: 30 }, { value: 50 }, { value: 100 }]}
            value={meta?.perPage || 10}
            onChange={e => onMetaChange('perPage', Number(e.target.value))}
            disabled={loading}
          />
        </FormGroup>
        <Button
          large
          style={style.pagination.button}
          onClick={() => onMetaChange('cursor', meta?.nextCursor)}
          disabled={!meta?.nextCursor}
          loading={!!loading}
        >
          Next
        </Button>
      </div>
    </>
  )
}

export { CursorPaginatedTable }
export default React.memo(CursorPaginatedTable)
